export type ExactKeywordNode =
  | { type: "term"; value: string; negated: boolean }
  | { type: "and" | "or"; children: ExactKeywordNode[] };

export type ExactKeywordParseResult =
  | { status: "empty" }
  | { status: "invalid" }
  | { status: "ok"; expression: ExactKeywordNode };

type Token =
  | { type: "and" }
  | { type: "or" }
  | { type: "lparen" }
  | { type: "rparen" }
  | { type: "not" }
  | { type: "term"; value: string };

function term(value: string, negated = false): ExactKeywordNode {
  return { type: "term", value, negated };
}

function combineAnd(parts: ExactKeywordNode[]): ExactKeywordNode {
  if (parts.length === 1) {
    return parts[0];
  }
  return { type: "and", children: parts };
}

function combineOr(parts: ExactKeywordNode[]): ExactKeywordNode {
  if (parts.length === 1) {
    return parts[0];
  }
  return { type: "or", children: parts };
}

function negate(node: ExactKeywordNode): ExactKeywordNode {
  if (node.type === "term") {
    return term(node.value, !node.negated);
  }
  return {
    type: node.type === "and" ? "or" : "and",
    children: node.children.map(negate),
  };
}

function hasPositiveTerm(node: ExactKeywordNode): boolean {
  if (node.type === "term") {
    return !node.negated;
  }
  return node.children.some(hasPositiveTerm);
}

function tokenize(input: string): Token[] | undefined {
  const tokens: Token[] = [];
  let i = 0;

  while (i < input.length) {
    const char = input[i];

    if (/\s/.test(char)) {
      i += 1;
      continue;
    }

    if (char === "(") {
      tokens.push({ type: "lparen" });
      i += 1;
      continue;
    }

    if (char === ")") {
      tokens.push({ type: "rparen" });
      i += 1;
      continue;
    }

    // Prefix NOT only — mid-token hyphens stay in the term (well-known).
    if (
      char === "-" &&
      i + 1 < input.length &&
      !/\s/.test(input[i + 1]) &&
      input[i + 1] !== ")"
    ) {
      tokens.push({ type: "not" });
      i += 1;
      continue;
    }

    if (char === "-") {
      return undefined;
    }

    if (char === '"') {
      i += 1;
      let value = "";
      while (i < input.length && input[i] !== '"') {
        value += input[i];
        i += 1;
      }
      if (i >= input.length || !value) {
        return undefined;
      }
      i += 1;
      tokens.push({ type: "term", value });
      continue;
    }

    let word = "";
    while (i < input.length) {
      const next = input[i];
      if (/\s/.test(next) || next === "(" || next === ")" || next === '"') {
        break;
      }
      if (next === "-") {
        const after = input[i + 1];
        if (word.length === 0) {
          break;
        }
        if (
          after !== undefined &&
          !/\s/.test(after) &&
          after !== ")" &&
          after !== "(" &&
          after !== '"'
        ) {
          word += next;
          i += 1;
          continue;
        }
        break;
      }
      word += next;
      i += 1;
    }

    if (!word) {
      return undefined;
    }

    const lower = word.toLowerCase();
    if (lower === "and") {
      tokens.push({ type: "and" });
    } else if (lower === "or") {
      tokens.push({ type: "or" });
    } else {
      tokens.push({ type: "term", value: word });
    }
  }

  return tokens;
}

class Parser {
  private index = 0;

  constructor(private readonly tokens: Token[]) {}

  parse(): ExactKeywordNode | undefined {
    if (this.tokens.length === 0) {
      return undefined;
    }
    const expression = this.parseOr();
    if (this.index !== this.tokens.length || !expression) {
      return undefined;
    }
    return expression;
  }

  private peek(): Token | undefined {
    return this.tokens[this.index];
  }

  private consume(): Token | undefined {
    const token = this.tokens[this.index];
    this.index += 1;
    return token;
  }

  private parseOr(): ExactKeywordNode | undefined {
    const parts: ExactKeywordNode[] = [];
    const first = this.parseAnd();
    if (!first) {
      return undefined;
    }
    parts.push(first);

    while (this.peek()?.type === "or") {
      this.consume();
      const next = this.parseAnd();
      if (!next) {
        return undefined;
      }
      parts.push(next);
    }

    return combineOr(parts);
  }

  private parseAnd(): ExactKeywordNode | undefined {
    const parts: ExactKeywordNode[] = [];
    const first = this.parseFactor();
    if (!first) {
      return undefined;
    }
    parts.push(first);

    let next = this.peek();
    while (
      next &&
      (next.type === "and" ||
        next.type === "term" ||
        next.type === "not" ||
        next.type === "lparen")
    ) {
      if (next.type === "and") {
        this.consume();
      }
      const factor = this.parseFactor();
      if (!factor) {
        return undefined;
      }
      parts.push(factor);
      next = this.peek();
    }

    return combineAnd(parts);
  }

  private parseFactor(): ExactKeywordNode | undefined {
    if (this.peek()?.type === "not") {
      this.consume();
      const primary = this.parsePrimary();
      if (!primary) {
        return undefined;
      }
      return negate(primary);
    }
    return this.parsePrimary();
  }

  private parsePrimary(): ExactKeywordNode | undefined {
    const next = this.peek();
    if (!next) {
      return undefined;
    }

    if (next.type === "lparen") {
      this.consume();
      const inner = this.parseOr();
      if (!inner || this.peek()?.type !== "rparen") {
        return undefined;
      }
      this.consume();
      return inner;
    }

    if (next.type !== "term") {
      return undefined;
    }
    this.consume();
    return term(next.value);
  }
}

/**
 * Parse an exact-string keyword expression into a domain AST.
 * Does not emit RSQL or wrap Notes/Transcript scope — callers do that.
 */
export default function parseExactKeywordSearch(
  query: string,
): ExactKeywordParseResult {
  const trimmed = query.trim();
  if (!trimmed) {
    return { status: "empty" };
  }

  const tokens = tokenize(trimmed);
  if (!tokens) {
    return { status: "invalid" };
  }

  const expression = new Parser(tokens).parse();
  if (!expression || !hasPositiveTerm(expression)) {
    return { status: "invalid" };
  }

  return { status: "ok", expression };
}
